/**
 * The Meta pixel, for measuring which ads lead to a signup, a published app and a
 * subscription. Nothing from Meta loads until the visitor has said yes, HERE.
 *
 * The other half lives on the landing site (landing_pages/saddle/app/components/
 * MetaPixel.tsx) and the two must agree on three things:
 *   - the pixel ID below and SITE.metaPixelId there;
 *   - a "no" is shared: `saddle_consent=denied` on .saddle-sites.com, so a visitor who
 *     declined on either site is not asked again on the other;
 *   - Global Privacy Control is a standing "no" — the privacy policy promises it.
 *
 * A "yes" is NOT shared, and must not be. Published apps are served on the same
 * registrable domain (app.saddle-sites.com) with their creators' own script, and any of
 * them can write a .saddle-sites.com cookie — so a shared `granted` is something a stranger
 * can forge to opt this visitor in without ever seeing a banner. A yes therefore counts only
 * from this origin's own storage, which no other subdomain can write, and a shared cookie is
 * believed only when it says no: a forged "no" can do nothing worse than measure less. The
 * price is that someone who accepted on the landing site is asked once more here.
 *
 * script-src is 'self' (infra/lib/saddle-web-stack.ts), so Meta's usual inline snippet
 * cannot run; `load()` is that snippet, bundled.
 *
 * Who is signed in reaches Meta only one way: `setPixelUser`, as a SHA-256 hash of the
 * account email and of the account id, made in this browser and handed to `init` (manual
 * advanced matching). The privacy policy (infra/site/privacy.html) says exactly that, and
 * scripts/verify-pixel-config.mjs checks it against the dataset's live config.
 */

import type { FunnelStep } from './funnel';
import { isTestVisit } from './testVisit';

/** Meta pixel (dataset) ID from Events Manager — the same one as the landing site. Empty = off. */
export const META_PIXEL_ID = '1001419716248228';

const CONSENT_COOKIE = 'saddle_consent';
/** This origin's own record of the answer: localStorage, which only this origin can write. */
const CONSENT_KEY = 'saddle_consent';
const CONSENT_MAX_AGE = 60 * 60 * 24 * 180; // Ask again after six months.
/**
 * Which question a stored yes answered. 2 is the banner that says a signed-in visitor's email
 * also goes to Meta, scrambled, and that the data may pass through Stape and a backup server
 * on Google Cloud on its way (September 2026). The yes given to the banner before it said
 * neither, so it is no answer to this one: it reads as unanswered and the visitor is asked
 * again. A no needs no version — it refused less than is asked now. Bump it whenever the
 * banner starts to say a yes covers MORE; a banner that says less needs no new yes.
 */
const CONSENT_VERSION = 2;
const SHARED_DOMAIN = 'saddle-sites.com';

export type Consent = 'granted' | 'denied';

type Fbq = ((...args: unknown[]) => void) & {
  callMethod?: (...args: unknown[]) => void;
  queue: unknown[];
  push: Fbq;
  loaded: boolean;
  version: string;
};

declare global {
  interface Window {
    fbq?: Fbq;
    _fbq?: Fbq;
  }
}

/** Dev and test builds never send anything, whatever the ID. */
export function pixelEnabled(): boolean {
  // A test visit (`test=1`, analytics/testVisit.ts) never reaches Meta.
  return Boolean(META_PIXEL_ID) && process.env.NODE_ENV === 'production' && !isTestVisit();
}

export function readConsent(): Consent | null {
  if ((navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl) {
    return 'denied';
  }
  // The shared cookie is read for a no and nothing else — see the header. A host-only and a
  // .saddle-sites.com cookie of the same name read back as two indistinguishable entries, so
  // any of them saying no is a no.
  const sharedNo = new RegExp(`(?:^|; )${CONSENT_COOKIE}=denied(?:;|$)`).test(document.cookie);
  const own = readOwnConsent();
  return sharedNo ? 'denied' : own;
}

/** The answer given on this origin, or null when there is none, it expired, or storage is unavailable. */
function readOwnConsent(): Consent | null {
  try {
    const record = JSON.parse(localStorage.getItem(CONSENT_KEY) ?? 'null') as {
      choice?: unknown;
      until?: unknown;
      v?: unknown;
    } | null;
    if (!record || typeof record.until !== 'number' || record.until <= Date.now()) return null;
    if (record.choice === 'denied') return 'denied';
    return record.choice === 'granted' && record.v === CONSENT_VERSION ? 'granted' : null;
  } catch {
    // Unreadable storage means no recorded yes: the visitor is asked, never assumed.
    return null;
  }
}

export function saveConsent(choice: Consent): void {
  try {
    localStorage.setItem(
      CONSENT_KEY,
      JSON.stringify({ choice, until: Date.now() + CONSENT_MAX_AGE * 1000, v: CONSENT_VERSION }),
    );
  } catch {
    /* Blocked storage records nothing: a yes then measures nothing, and is asked for again. */
  }
  // Only a no is shared with the landing site; a shared yes is forgeable (see the header).
  if (choice !== 'denied') return;
  // Shared with the landing site only on the real domain; elsewhere a host-only cookie.
  const onShared = location.hostname === SHARED_DOMAIN || location.hostname.endsWith(`.${SHARED_DOMAIN}`);
  const domain = onShared ? `; Domain=.${SHARED_DOMAIN}` : '';
  // Secure wherever it can be: an http page (tests, a LAN preview) would drop the cookie.
  const secure = location.protocol === 'https:' ? '; Secure' : '';
  document.cookie =
    `${CONSENT_COOKIE}=${choice}; Max-Age=${CONSENT_MAX_AGE}; Path=/; SameSite=Lax${secure}${domain}`;
}

/**
 * Meta's base snippet, as a function. Idempotent.
 *
 * Route PageViews are fbevents' job, not ours: once this script loads it hooks pushState,
 * replaceState and popstate and tracks one per navigation, and it drops any explicit
 * PageView after the first for the pixel. So ConsentBanner sends exactly one, and nothing
 * else here should. Do NOT set `fbq.disablePushState` unless you also set
 * `fbq.allowDuplicatePageViews = true` and send a PageView per route yourself — alone it
 * leaves one PageView per page load and every in-app navigation unmeasured.
 */
function load(id: string): void {
  if (window.fbq) return;
  const fbq = function (...args: unknown[]) {
    if (fbq.callMethod) fbq.callMethod(...args);
    else fbq.queue.push(args);
  } as Fbq;
  fbq.push = fbq;
  fbq.loaded = true;
  fbq.version = '2.0';
  fbq.queue = [];
  window.fbq = fbq;
  window._fbq = fbq;

  const script = document.createElement('script');
  script.async = true;
  script.src = 'https://connect.facebook.net/en_US/fbevents.js';
  document.head.appendChild(script);

  /*
   * Off BEFORE init, because fbevents reads the queue in order. Left on, the pixel's
   * automatic configuration (InferredEvents and the rest of AutomaticSetup) reports each
   * button press with its text — which on Home and My Apps is a project's name and
   * description — and is one route by which fbevents 2.9.x (read 2026-09-28) scrapes a
   * clicked form for Automatic Advanced Matching. The privacy policy says Meta gets neither:
   * the only personal data it gets is the hashed email and account id that `setPixelUser`
   * hands over below.
   * This line does NOT switch off the AutomaticMatching opt-in itself (it is not one of the
   * AutomaticSetup features), so Automatic Advanced Matching must also be off for this
   * dataset in Events Manager; scripts/verify-pixel-config.mjs reports it SERIOUS until it is.
   */
  fbq('set', 'autoConfig', false, id);
  fbq('init', id);
}

/** Loads the pixel if it is on and the visitor said yes. Returns whether it is live. */
export function startPixel(): boolean {
  if (!pixelEnabled() || readConsent() !== 'granted') return false;
  load(META_PIXEL_ID);
  const fbq = window.fbq;
  if (!fbq || told(fbq).silenced) return false;
  void matchPixel();
  return true;
}

/**
 * The only user data the builder hands Meta, each a SHA-256 hex digest made in this browser:
 * `em` the account email, `external_id` the account id. The privacy policy names each one,
 * and two checks read THIS line to hold it to that — infra/lambda/tests/privacyNamesProviders
 * .test.ts and scripts/verify-pixel-config.mjs — so a key added here without words in the
 * policy fails both. The init payload is typed by it, so a key cannot be sent without it.
 */
export const MATCH_KEYS = ['em', 'external_id'] as const;
type MatchKey = (typeof MATCH_KEYS)[number];

/** The signed-in person, as the session tells it. Only ever sent on, hashed, by `matchPixel`. */
export interface PixelUser {
  id: string;
  email: string;
}

/** Who the session last said is signed in. Held in memory, and sent nowhere without a yes. */
let signedIn: PixelUser | null = null;

/**
 * What THIS page's pixel has been told. Keyed by the fbq it was told through, because
 * fbevents keeps its copy for the life of the page and a new page starts a new fbq.
 *   - id: the account whose hashes went into `init`, or null while it names nobody.
 *   - pending: the account being hashed right now, so a burst of events hashes once.
 *   - silenced: the named person left; nothing more is sent from this page.
 */
interface Told {
  id: string | null;
  pending: string | null;
  silenced: boolean;
}
const toldBy = new WeakMap<Fbq, Told>();
function told(fbq: Fbq): Told {
  let state = toldBy.get(fbq);
  if (!state) toldBy.set(fbq, (state = { id: null, pending: null, silenced: false }));
  return state;
}

/**
 * Tell the pixel who is signed in, or that nobody is. Called by the session on every change,
 * at the moment it happens (state/session.tsx), and safe to call at any time: without a yes
 * it only remembers, and hashes and sends nothing.
 */
export async function setPixelUser(user: PixelUser | null): Promise<void> {
  signedIn = user?.id ? { id: user.id, email: user.email ?? '' } : null;
  await matchPixel();
}

/**
 * Brings the live pixel in line with who is signed in. Never throws.
 *
 * Someone signs in: `fbq('init', id, { em, external_id })`, each a SHA-256 hex digest made
 * here with crypto.subtle, so the plain email and id never reach Meta's script. fbevents
 * takes an already-hashed value as it is (a 64-character hex string is left alone).
 *
 * Someone signs out: fbevents cannot be told. It takes user data from a second `init` only
 * while the pixel holds none; after that it logs "Duplicate Pixel ID" and keeps what it has
 * for the life of the page (fbevents.js 2.9.x, checked 2026-09-28) — so the hashes can be
 * neither cleared nor replaced by the next person to sign in on this computer. What it does
 * support is `fbq('consent', 'revoke')`, which holds every later event, its own route
 * PageViews included, until a grant this page never gives. So a sign-out silences the pixel
 * for the rest of the page, and the next page load starts one that knows nobody. The events
 * lost are those of a signed-out visitor between a sign-out and a reload.
 */
async function matchPixel(): Promise<void> {
  try {
    const fbq = window.fbq;
    if (!fbq || !pixelEnabled() || readConsent() !== 'granted') return;
    const state = told(fbq);
    const user = signedIn;
    if (state.silenced || state.id === (user?.id ?? null)) return;
    if (state.id !== null) {
      fbq('consent', 'revoke');
      state.silenced = true;
      return;
    }
    if (!user || state.pending === user.id) return;
    state.pending = user.id;
    try {
      // Meta's normalisation for an email: trimmed and lower-cased, then hashed.
      const email = user.email.trim().toLowerCase();
      const [em, externalId] = await Promise.all([email ? sha256Hex(email) : null, sha256Hex(user.id)]);
      // The session may have moved on while this hashed: a sign-out, or a different person.
      if (signedIn?.id !== user.id || window.fbq !== fbq || state.silenced || state.id !== null) return;
      // A token without an email claim still matches by account id, never by the hash of ''.
      const match: Partial<Record<MatchKey, string>> = { external_id: externalId };
      if (em) match.em = em;
      fbq('init', META_PIXEL_ID, match);
      state.id = user.id;
    } finally {
      if (state.pending === user.id) state.pending = null;
    }
  } catch {
    // A browser that cannot hash (no crypto.subtle outside a secure context) sends no user
    // data at all. Never the plain value in its place.
  }
}

async function sha256Hex(value: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(value));
  return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('');
}

/*
 * InitiateCheckout is the event the ads optimize for (the owner, 2026-09-25): a non-subscriber
 * reached the paywall at the Plan screen's Build button. StartTrial is the return from Stripe,
 * in place of the Subscribe sent there before: a builder checkout is a 7-day trial, and a trial is
 * not yet a paying subscriber. The trial converting is Subscribe, and the browser is not there when
 * it happens, so that one belongs to the server (the Conversions API). So does the Subscribe of an
 * account that had had its trial (one per account, 2026-09-29) and was sold the plan from day one:
 * its return sends nothing (state/trial.ts `reportStartTrial`).
 */
type Standard = 'PageView' | 'CompleteRegistration' | 'Subscribe' | 'InitiateCheckout' | 'StartTrial';
/*
 * Custom events: ours, named by us, sent with `trackCustom`. The funnel's step events
 * (analytics/funnel.ts) are among them, so the owner can see in Events Manager where the ad
 * visitors who said yes stop; they carry nothing but the step and an enumerated prop or two.
 */
type Custom = 'AppPublished' | FunnelStep;
const STANDARD = new Set<string>(['PageView', 'CompleteRegistration', 'Subscribe', 'InitiateCheckout', 'StartTrial']);

/**
 * Send one event, or do nothing. Never throws: measurement must not be able to break a
 * signup or a publish, which is where these calls sit.
 *
 * Says whether the event went to the pixel. The events sent once per draft or per checkout
 * (state/trial.ts) are recorded as sent only when they were: recorded on a press made before the
 * banner was answered, the draft's one InitiateCheckout was spent on nothing, and the press after
 * a later yes sent nothing either (review, 2026-09-29).
 */
export function trackPixel(event: Standard | Custom, params?: Record<string, unknown>): boolean {
  try {
    // fbevents attaches the page's full URL to every event, and ?idea= is the visitor's typed
    // sentence. Nothing leaves while it is there — the privacy policy says Meta never gets a prompt.
    // Here, not in each caller: the funnel's idea_viewed went straight past the banner's guard.
    if (new URLSearchParams(window.location.search).has('idea')) return false;
    // Not live also covers a pixel silenced by a sign-out (see matchPixel).
    if (!startPixel()) return false;
    const fbq = window.fbq;
    if (!fbq) return false;
    const method = STANDARD.has(event) ? 'track' : 'trackCustom';
    if (params) fbq(method, event, params);
    else fbq(method, event);
    return true;
  } catch {
    /* see above */
    return false;
  }
}
